import React from "react";
import Spline from "@splinetool/react-spline";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

const headline = "ACTIVATIONS THAT CUT THROUGH";

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.2 },
  },
};

const word = {
  hidden: { opacity: 0, y: 28, rotateX: -30 },
  show: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: { duration: 0.65, ease: [0.2, 0.8, 0.2, 1] },
  },
};

export default function Hero() {
  const reduce = useReducedMotion();

  const scrollTo = (hash) => (e) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0F1E] isolate"
      data-testid="hero-section"
    >
      {/* Grid + radial glows */}
      <div className="absolute inset-0 axe-grid opacity-60" aria-hidden="true" />
      <div className="absolute inset-0 axe-radial" aria-hidden="true" />

      {/* Spline 3D scene */}
      <div className="absolute inset-0" aria-hidden="true">
        <Spline
          scene="https://prod.spline.design/VC61wSeilKo3Gh5r/scene.splinecode"
          style={{ width: "100%", height: "100%" }}
        />
      </div>

      {/* Bottom vignette so text reads well */}
      <div
        className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#0A0F1E] via-[#0A0F1E]/60 to-transparent pointer-events-none"
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-32 sm:pt-36 lg:pt-44 pb-20 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur px-4 py-1.5 text-[11px] font-medium tracking-[0.2em] text-white/80 uppercase pointer-events-auto"
          data-testid="hero-eyebrow"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#60A5FA] animate-pulse" />
          BTL · Experiential · Activations
        </motion.div>

        <motion.h1
          variants={container}
          initial={reduce ? "show" : "hidden"}
          animate="show"
          aria-label="Activations that cut through"
          className="font-display mt-7 text-white text-[44px] leading-[0.95] sm:text-6xl lg:text-[92px] lg:leading-[0.92] font-bold uppercase max-w-5xl"
          data-testid="hero-headline"
        >
          {headline.split(" ").map((w, i) => (
            <motion.span
              key={i}
              variants={word}
              className="inline-block mr-[0.25em]"
            >
              {w === "CUT" ? (
                <span className="relative inline-block">
                  <span className="relative z-10 text-[#E02424]">{w}</span>
                  <span
                    className="absolute -inset-x-2 top-1/2 h-[6px] bg-[#E02424]/30 blur-md"
                    aria-hidden="true"
                  />
                </span>
              ) : (
                w
              )}
            </motion.span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-white/70 leading-relaxed"
          data-testid="hero-subheadline"
        >
          BTL Marketing &amp; Experiential Events that move people and drive
          results.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.55 }}
          className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 pointer-events-auto"
        >
          <a
            href="#contact"
            onClick={scrollTo("#contact")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#E02424] px-7 py-3.5 text-sm font-semibold text-white hover:bg-[#c91f1f] transition-all shadow-[0_20px_40px_-20px_rgba(224,36,36,0.9)] hover:shadow-[0_20px_60px_-15px_rgba(224,36,36,1)]"
            data-testid="hero-cta-start-project"
          >
            Start a Project
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-1"
            />
          </a>
          <a
            href="#services"
            onClick={scrollTo("#services")}
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/[0.02] backdrop-blur px-7 py-3.5 text-sm font-semibold text-white hover:border-[#60A5FA] hover:bg-white/[0.05] transition-all"
            data-testid="hero-cta-see-work"
          >
            <Play size={14} className="text-[#60A5FA]" fill="currentColor" />
            See Our Work
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-16 sm:mt-24 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs sm:text-sm text-white/50 font-medium tracking-widest uppercase"
        >
          <span className="flex items-center gap-2">
            <span className="h-px w-8 bg-[#60A5FA]" />
            Retail Activations
          </span>
          <span>In-Store Promotions</span>
          <span>Live Brand Experiences</span>
          <span className="hidden sm:inline">Guerrilla Campaigns</span>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-white/50 pointer-events-none"
        aria-hidden="true"
      >
        <span>Scroll</span>
        <span className="relative h-8 w-[1.5px] bg-white/10 overflow-hidden">
          <motion.span
            animate={{ y: ["-100%", "200%"] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-0 left-0 h-3 w-full bg-[#60A5FA]"
          />
        </span>
      </motion.div>
    </section>
  );
}
